import React, { useEffect, useRef, useState } from "react";
import {  useNavigate, NavLink } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  ShieldCheck,
  BookOpen,
  HelpCircle,
  Newspaper,
  Building2,
  Mail,
  Phone,
  ArrowUpRight,
} from "lucide-react";

import logo from "src/assets/images/crypsyn-full-logo.png";
import LanguageSwitcher from "../components/language-switcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";


/* =========================================================
   Dropdown Item
========================================================= */

const DropdownItem = ({
  icon: Icon,
  title,
  description,
  to,
  external = false,
  onClick,
}) => {
  const content = (
    <>
      <div
        className="
          flex h-10 w-10 shrink-0
          items-center justify-center
          rounded-xl
          bg-slate-50
          text-slate-600
          transition-colors
          group-hover:bg-blue-50
          group-hover:text-blue-600
        "
      >
        <Icon size={18} strokeWidth={1.8} />
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-600">
            {title}
          </p>

          {external && (
            <ArrowUpRight
              size={13}
              className="text-slate-400 group-hover:text-blue-500"
            />
          )}
        </div>

        {description && (
          <p className="mt-0.5 text-xs leading-5 text-slate-500">
            {description}
          </p>
        )}
      </div>
    </>
  );

  // const className="group flex items-start gap-3 rounded-xl p-3 transition-all duration-200 hover:bg-slate-50";

const className = ({ isActive }) => `
  group flex items-start gap-3 rounded-xl p-3
  transition-all duration-200
  ${
    isActive
      ? "bg-slate-100 text-slate-900 ring-1 ring-slate-200 hover:bg-slate-100"
      : "text-slate-700 hover:bg-slate-50"
  }
`;

  if (to) {
    return (
      <NavLink
        to={to}
        onClick={onClick}
        className={className}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
      >
        {content}
      </NavLink>
    );
  }

  return (
    <NavLink to={to} onClick={onClick} className={className}>
      {content}
    </NavLink>
  );
};

/* =========================================================
   Header
========================================================= */

const Header = () => {
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);

  const headerRef = useRef(null);
const { t } = useTranslation("header");
 

  /* -------------------------------------------------------
     Scroll effect
  ------------------------------------------------------- */

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* -------------------------------------------------------
     Lock body scroll on mobile menu
  ------------------------------------------------------- */

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  /* -------------------------------------------------------
     Close dropdown when clicking outside
  ------------------------------------------------------- */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target)
      ) {
        setOpenDesktopDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* -------------------------------------------------------
     Escape key
  ------------------------------------------------------- */

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenDesktopDropdown(null);
        setOpenMobileDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  /* -------------------------------------------------------
     Navigation helpers
  ------------------------------------------------------- */

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setOpenMobileDropdown(null);
  };

  const toggleDesktopDropdown = (name) => {
    setOpenDesktopDropdown((current) =>
      current === name ? null : name
    );
  };

  const toggleMobileDropdown = (name) => {
    setOpenMobileDropdown((current) =>
      current === name ? null : name
    );
  };

  /* =======================================================
     Render
  ======================================================= */

  return (
    <>
  <header
  ref={headerRef}
  id="top"
  
  className={`
    fixed left-1/2 top-4 z-50
    w-[calc(100%-2rem)]
    max-w-[1280px]
    -translate-x-1/2
    rounded-[22px]
    border
    transition-all duration-300
    ${
      isScrolled
        ? "border-white/80 bg-white/65 shadow-[0_12px_40px_rgba(15,23,42,0.10)] backdrop-blur-2xl"
        : "border-white/60 bg-white/45 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-xl"
    }
  `}
  
>
        <div  className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-[68px] items-center justify-between lg:h-[76px]">

            {/* =================================================
                Logo
            ================================================= */}

            <NavLink
              to="/"
              onClick={closeMobileMenu}
              className="
                flex shrink-0 items-center
                transition-opacity duration-200
                hover:opacity-85
              "
            >
              <img
                src={logo}
                alt="Crypsyn"
                className="w-[108px] sm:w-[120px]"
              />
            </NavLink>

            {/* =================================================
                Desktop Navigation
            ================================================= */}

            <nav className="hidden lg:flex items-center gap-1">

              {/* Features */}
              <NavLink
                to="/"
              className={({ isActive }) => `
  rounded-lg px-3.5 py-1.5
  text-sm font-medium
  transition-all duration-200
  ${
    isActive
      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:from-blue-700 hover:to-indigo-700"
      : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
  }
`}
              >
                {/* Features */}
                 {t("nav.features")}
              </NavLink>

              {/* Security */}
              <NavLink
                to="/security"
                // className="
                //   rounded-xl px-3.5 py-2.5
                //   text-sm font-medium text-slate-600
                //   transition-all duration-200
                //   hover:bg-slate-50
                //   hover:text-blue-600
                // "
                    className={({ isActive }) => `
  rounded-lg px-3.5 py-1.5
  text-sm font-medium
  transition-all duration-200
  ${
    isActive
      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:from-blue-700 hover:to-indigo-700"
      : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
  }
`}
              >
                {/* Security */}
                 {t("nav.security")}
              </NavLink>

              {/* Pricing */}
              <NavLink
                to="/pricing-plan"
                // className="
                //   rounded-xl px-3.5 py-2.5
                //   text-sm font-medium text-slate-600
                //   transition-all duration-200
                //   hover:bg-slate-50
                //   hover:text-blue-600
                // "
                    className={({ isActive }) => `
  rounded-lg px-3.5 py-1.5
  text-sm font-medium
  transition-all duration-200
  ${
    isActive
      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:from-blue-700 hover:to-indigo-700"
      : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
  }
`}
              >
                {/* Pricing */}
                 {t("nav.pricing")}
              </NavLink>

              {/* =================================================
                  Resources Dropdown
              ================================================= */}

              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    toggleDesktopDropdown("resources")
                  }
                  className={`
                    flex items-center gap-1
                    rounded-xl px-3.5 py-2.5
                    text-sm font-medium
                    transition-all duration-200
                    ${
                      openDesktopDropdown === "resources"
                        ? "bg-slate-50 text-blue-600"
                        : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
                    }
                  `}
                  aria-expanded={
                    openDesktopDropdown === "resources"
                  }
                >
                  {/* Resources */}
                   {t("nav.resources")}

                  <ChevronDown
                    size={15}
                    className={`
                      transition-transform duration-200
                      ${
                        openDesktopDropdown === "resources"
                          ? "rotate-180"
                          : ""
                      }
                    `}
                  />
                </button>

                {openDesktopDropdown === "resources" && (
                  <div
                    className="
                      absolute left-1/2 top-[calc(100%+12px)]
                      w-[320px]
                      -translate-x-1/2
                      rounded-2xl
                      border border-slate-200
                      bg-white
                      p-2
                      shadow-[0_20px_60px_rgba(15,23,42,0.12)]
                      animate-[dropdownIn_.18s_ease-out]
                    "
                  >
                    <DropdownItem
                      icon={BookOpen}
                      title={t("resourcesMenu.documentation.title")}
                      description={t("resourcesMenu.documentation.description")}
                      to="/documentation"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />

                    <DropdownItem
                      icon={HelpCircle}
                      title={t("resourcesMenu.faq.title")}
                      description={t("resourcesMenu.faq.description")}
                      to="/faq"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />

                    <DropdownItem
                      icon={Newspaper}
                      title={t("resourcesMenu.blog.title")}
                      description={t("resourcesMenu.blog.description")}
                      to="/blog"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />

                    <DropdownItem
                      icon={Mail}
                      title={t("resourcesMenu.helpCenter.title")}
                      description={t("resourcesMenu.helpCenter.description")}
                      to="/help-center"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />
                  </div>
                )}
              </div>

              {/* =================================================
                  Company Dropdown
              ================================================= */}

              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    toggleDesktopDropdown("company")
                  }
                  className={`
                    flex items-center gap-1
                    rounded-xl px-3.5 py-2.5
                    text-sm font-medium
                    transition-all duration-200
                    ${
                      openDesktopDropdown === "company"
                        ? "bg-slate-50 text-blue-600"
                        : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
                    }
                  `}
                  aria-expanded={
                    openDesktopDropdown === "company"
                  }
                >
                  {/* Company */}
                   {t("nav.company")}

                  <ChevronDown
                    size={15}
                    className={`
                      transition-transform duration-200
                      ${
                        openDesktopDropdown === "company"
                          ? "rotate-180"
                          : ""
                      }
                    `}
                  />
                </button>

                {openDesktopDropdown === "company" && (
                  <div
                    className="
                      absolute left-1/2 top-[calc(100%+12px)]
                      w-[330px]
                      -translate-x-1/2
                      rounded-2xl
                      border border-slate-200
                      bg-white
                      p-2
                      shadow-[0_20px_60px_rgba(15,23,42,0.12)]
                      animate-[dropdownIn_.18s_ease-out]
                    "
                  >
                    <DropdownItem
                      icon={Building2}
                      title={t("companyMenu.about.title")}
                      description={t("companyMenu.about.description")}
                      to="/about-us"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />

                    <DropdownItem
                      icon={ShieldCheck}
                      title={t("companyMenu.mailfinance.title")}
                      description={t("companyMenu.mailfinance.description")}
                      to="/mailfinance-ag"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />

                    <DropdownItem
                      icon={Phone}
                      title={t("companyMenu.contact.title")}
                      description={t("companyMenu.contact.description")}
                      to="/contact"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    />

                    {/* <DropdownItem
                      icon={BriefcaseBusiness}
                      title={t("companyMenu.careers.title")}
                      description={t("companyMenu.careers.description")}
                      to="/careers"
                      onClick={() =>
                        setOpenDesktopDropdown(null)
                      }
                    /> */}
                  </div>
                )}
              </div>
            </nav>

            {/* =================================================
                Desktop CTA
            ================================================= */}

            <div className="hidden lg:flex items-center gap-2">

                <LanguageSwitcher />


              <button
                type="button"
                onClick={() =>
                  navigate("/signin")
                }
                className="
                  rounded-xl
                  px-4 py-2.5
                  text-sm font-semibold
                  text-slate-600
                  transition-all duration-200
                  hover:bg-slate-50
                  hover:text-blue-600
                "
              >
                {/* Sign In */}
                {t("actions.signIn")}
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate("/signup")
                }
                className="
                  rounded-xl
                  bg-gradient-to-r
                  from-blue-600 to-indigo-600
                  px-5 py-2.5
                  text-sm font-semibold
                  text-white
                  shadow-md shadow-blue-600/20
                  transition-all duration-200
                  hover:-translate-y-0.5
                  hover:from-blue-700
                  hover:to-indigo-700
                  hover:shadow-lg hover:shadow-blue-600/25
                  active:translate-y-0
                "
              >
                {/* Get Started */}
                {t("actions.getStarted")}
              </button>
            </div>

            {/* =================================================
                Mobile Menu Button
            ================================================= */}

            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="
                flex lg:hidden
                h-10 w-10
                items-center justify-center
                rounded-xl
                border border-slate-200
                bg-white
                text-slate-700
                shadow-sm
                transition-all
                hover:bg-slate-50
                active:scale-95
              "
              aria-label={
                isMenuOpen
                  ? "Close navigation"
                  : "Open navigation"
              }
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <X size={21} />
              ) : (
                <Menu size={21} />
              )}
            </button>
          </div>
        </div>

        {/* =====================================================
            Mobile Menu
        ===================================================== */}

        <div
          className={`
            lg:hidden
            overflow-hidden
            border-t border-slate-200/80
            bg-white
            transition-all duration-300
            ${
              isMenuOpen
                ? "max-h-[calc(100vh-68px)] opacity-100"
                : "max-h-0 opacity-0"
            }
          `}
        >
          <div className="max-h-[calc(100vh-68px)] overflow-y-auto px-4 py-5 sm:px-6">

            {/* Mobile Features */}
            <NavLink
              to="/"
              onClick={closeMobileMenu}
              className="
                block rounded-xl px-4 py-3
                text-sm font-medium text-slate-700
                hover:bg-slate-50 hover:text-blue-600
              "
            >
              {/* Features */}
              {t("nav.features")}
            </NavLink>

            {/* Mobile Security */}
            <NavLink
              to="/security"
              onClick={closeMobileMenu}
              className="
                block rounded-xl px-4 py-3
                text-sm font-medium text-slate-700
                hover:bg-slate-50 hover:text-blue-600
              "
            >
              {/* Security */}
              {t("nav.security")}
            </NavLink>

            {/* Mobile Pricing */}
            <NavLink
              to="/pricing-plan"
              onClick={closeMobileMenu}
              className="
                block rounded-xl px-4 py-3
                text-sm font-medium text-slate-700
                hover:bg-slate-50 hover:text-blue-600
              "
            >
              {/* Pricing */}
              {t("nav.pricing")}
            </NavLink>

            {/* =================================================
                Mobile Resources
            ================================================= */}

            <div className="mt-1">
              <button
                type="button"
                onClick={() =>
                  toggleMobileDropdown("resources")
                }
                className="
                  flex w-full items-center justify-between
                  rounded-xl px-4 py-3
                  text-sm font-medium text-slate-700
                  hover:bg-slate-50
                "
              >
                {/* Resources */}
                {t("nav.resources")}

                <ChevronDown
                  size={17}
                  className={`
                    transition-transform duration-200
                    ${
                      openMobileDropdown === "resources"
                        ? "rotate-180 text-blue-600"
                        : ""
                    }
                  `}
                />
              </button>

              {openMobileDropdown === "resources" && (
                <div className="ml-2 mt-1 border-l border-slate-200 pl-3">

                  <NavLink
                    to="/documentation"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <BookOpen size={17} />
                    {/* Documentation */}
                    {t("resourcesMenu.documentation.title")}
                  </NavLink>

                  <NavLink
                  to="/faq"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <HelpCircle size={17} />
                    {/* FAQ */}
                    {t("resourcesMenu.faq.title")}
                  </NavLink>

                  <NavLink
                    to="/blog"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <Newspaper size={17} />
                    {/* Blog */}
                    {t("resourcesMenu.blog.title")}
                  </NavLink>

                  <NavLink
                    to="/help-center"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <Mail size={17} />
                    {/* Help Center */}
                    {t("resourcesMenu.helpCenter.title")}
                  </NavLink>
                </div>
              )}
            </div>

            {/* =================================================
                Mobile Company
            ================================================= */}

            <div className="mt-1">
              <button
                type="button"
                onClick={() =>
                  toggleMobileDropdown("company")
                }
                className="
                  flex w-full items-center justify-between
                  rounded-xl px-4 py-3
                  text-sm font-medium text-slate-700
                  hover:bg-slate-50
                "
              >
                {/* Company */}
                {t("nav.company")}

                <ChevronDown
                  size={17}
                  className={`
                    transition-transform duration-200
                    ${
                      openMobileDropdown === "company"
                        ? "rotate-180 text-blue-600"
                        : ""
                    }
                  `}
                />
              </button>

              {openMobileDropdown === "company" && (
                <div className="ml-2 mt-1 border-l border-slate-200 pl-3">

                  <NavLink
                    to="/about-us"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <Building2 size={17} />
                    {/* About Crypsyn */}
                    {t("companyMenu.about.title")}
                  </NavLink>

                  <NavLink
                    to="/mailfinance-ag"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <ShieldCheck size={17} />
                    {/* Mailfinance AG */}
                    {t("companyMenu.mailfinance.title")}
                  </NavLink>

                  <NavLink
                    to="/contact"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <Phone size={17} />
                    {/* Contact */}
                    {t("companyMenu.contact.title")}
                  </NavLink>

                  {/* <Link
                    to="/careers"
                    onClick={closeMobileMenu}
                    className="
                      flex items-center gap-3
                      rounded-xl px-4 py-3
                      text-sm text-slate-600
                      hover:bg-blue-50 hover:text-blue-600
                    "
                  >
                    <BriefcaseBusiness size={17} />
                    {t("companyMenu.careers.title")}
                  </Link> */}
                </div>
              )}

                            <LanguageSwitcher mobile />

            </div>

            {/* Divider */}
            <div className="my-5 h-px bg-slate-200" />

            {/* =================================================
                Mobile CTA
            ================================================= */}

            <div className="grid grid-cols-2 gap-3 pb-4">


              <NavLink
                to="/signin"
                onClick={closeMobileMenu}
                className="
                  flex items-center justify-center
                  rounded-xl
                  border border-slate-200
                  bg-white
                  px-4 py-3
                  text-sm font-semibold
                  text-slate-700
                  shadow-sm
                  transition-all
                  hover:bg-slate-50
                "
              >
                {/* Sign In */}
                {t("actions.signIn")}
              </NavLink>

              <NavLink
                to="/signup"
                onClick={closeMobileMenu}
                className="
                  flex items-center justify-center
                  rounded-xl
                  bg-gradient-to-r
                  from-blue-600 to-indigo-600
                  px-4 py-3
                  text-sm font-semibold
                  text-white
                  shadow-md shadow-blue-600/20
                "
              >
                {/* Get Started */}
                {t("actions.getStarted")}
              </NavLink>
            </div>

            {/* Mobile Trust */}
            <div
              className="
                mb-2 flex items-center justify-center gap-2
                rounded-xl
                border border-blue-100
                bg-blue-50/60
                px-4 py-3
                text-xs font-medium text-blue-700
              "
            >
              <ShieldCheck size={15} />
              {/* Swiss Privacy · Secure by Design */}
              {t("badge.text")}
            </div>
          </div>
        </div>
      </header>

    </>
  );
};

export default Header;